import React, { useMemo } from "react";
import Head from "next/head";
import Image from "next/image";
import cn from "classnames";
import styles from "../styles/layout.light.module.css";
import { AppSettings } from "../lib/get-app-settings";
import isDarkColor from "../lib/is-dark-color";
import AtlasSnippet from "./atlas-snippet";
import getBaseUrl from "../lib/get-base-url";
import t from "../texts/t";
import { createSchemeCss } from "@atlas/tool/box/style/createSchemeStyleSheet";
import LayoutSidebarLight, {
  TLayoutSidebarLightLink,
} from "./layout-sidebar.light";
import type { Section } from "../lib/load-categories";
import type { Article } from "../lib/load-article";
import LayoutFooter from "./layout-footer";

type TLightLayoutProps = {
  className?: string;
  pageTitle?: string;
  isCustomPageTitle?: boolean;
  articleCategoryName?: string;
  articleLocale?: string | null;
  appSettings: AppSettings;
  pageDescription?: string;
  children: React.ReactNode;
  searchValue?: string;
  hasTitle?: boolean;
  canonicalUrl?: string | null;
  seoImageUrl?: string;
  sidebar?: React.ReactNode;
  contentWidth?: number;
  hideFooter?: boolean;
  noSpacing?: boolean;
} & (
  | {
      categories: Section[];
      articles: Article[];
      selectedArticleSlug?: string;
      links?: undefined;
    }
  | {
      categories?: undefined;
      articles?: undefined;
      selectedArticleSlug?: undefined;
      links: TLayoutSidebarLightLink[];
    }
);

export default function LightLayout({
  className,
  pageTitle,
  isCustomPageTitle,
  articleCategoryName,
  articleLocale,
  appSettings,
  pageDescription,
  children,
  searchValue,
  hasTitle,
  canonicalUrl,
  seoImageUrl,
  sidebar,
  contentWidth,
  hideFooter,
  noSpacing,
  ...layoutProps
}: TLightLayoutProps) {
  const hcSuffix = appSettings.title;

  const ssrLogoUrl = appSettings.logoUrl || appSettings.logoDarkUrl;

  pageDescription = pageDescription
    ? pageDescription
    : hcSuffix
    ? t("Knowledge base of {0}", appSettings.language, [hcSuffix])
    : t("Knowledge base", appSettings.language);

  const [
    primaryInvertedColorLight,
    secondaryInvertedColorLight,
    primaryInvertedColorDark,
    secondaryInvertedColorDark,
    linkColorLight,
    linkColorDark,
    highlightColorLight,
    highlightColorDark,
  ] = useMemo(() => {
    const light = (opacity = 1) => `rgba(255, 255, 255, ${opacity})`;
    const dark = (opacity = 1) => `rgba(0, 0, 0, ${opacity})`;
    return [
      isDarkColor(appSettings.primaryColorLight) ? light : dark,
      isDarkColor(appSettings.secondaryColorLight) ? light : dark,
      isDarkColor(appSettings.primaryColorDark) ? light : dark,
      isDarkColor(appSettings.secondaryColorDark) ? light : dark,
      isDarkColor(appSettings.secondaryColorLight) ? "#6666ff" : "#0000ff",
      isDarkColor(appSettings.secondaryColorDark) ? "#6666ff" : "#0000ff",
      isDarkColor(appSettings.secondaryColorLight) ? "#ffffff" : "#262626",
      isDarkColor(appSettings.secondaryColorDark) ? "#ffffff" : "#262626",
    ];
  }, [
    appSettings.primaryColorLight,
    appSettings.secondaryColorLight,
    appSettings.primaryColorDark,
    appSettings.secondaryColorDark,
  ]);

  const isSecondaryLightIsActuallyDark = isDarkColor(
    appSettings.secondaryColorLight
  );
  const isSecondaryDarkIsActuallyDark = isDarkColor(
    appSettings.secondaryColorDark
  );

  // TODO: It seems this calls extra hooks
  const colorsCss = useMemo(
    () =>
      createSchemeCss(
        {
          primary: [
            appSettings.primaryColorLight,
            appSettings.primaryColorDark,
          ],
          primaryInverted: [
            primaryInvertedColorLight(),
            primaryInvertedColorDark(),
          ],
          primaryInverted95: [
            primaryInvertedColorLight(0.95),
            primaryInvertedColorDark(0.95),
          ],
          primaryInverted55: [
            primaryInvertedColorLight(0.55),
            primaryInvertedColorDark(0.55),
          ],
          primaryInverted50: [
            primaryInvertedColorLight(0.5),
            primaryInvertedColorDark(0.5),
          ],
          primaryInverted24: [
            primaryInvertedColorLight(0.24),
            primaryInvertedColorDark(0.24),
          ],
          secondary: [
            appSettings.secondaryColorLight,
            appSettings.secondaryColorDark,
          ],
          secondaryInverted: [
            secondaryInvertedColorLight(),
            secondaryInvertedColorDark(),
          ],
          secondaryInverted85: [
            secondaryInvertedColorLight(0.85),
            secondaryInvertedColorDark(0.85),
          ],
          secondaryInverted55: [
            secondaryInvertedColorLight(0.55),
            secondaryInvertedColorDark(0.55),
          ],
          secondaryInverted30: [
            secondaryInvertedColorLight(0.3),
            secondaryInvertedColorDark(0.3),
          ],
          search: [
            isSecondaryLightIsActuallyDark ? "#222222" : "#ffffff",
            isSecondaryDarkIsActuallyDark ? "#222222" : "#ffffff",
          ],
          filledTile: ["#ffffff", "#ffffff"],
          tileIcon: ["#fbf9f6", "#fbf9f6"],
          tileMarker: ["#fbf9f6", "#fbf9f6"],
          tileTitle: ["#242424", "#e5e5e5"],
          tileText: ["#747474", "#747474"],
          link: [linkColorLight, linkColorDark],
          highlight: [highlightColorLight, highlightColorDark],
        },
        "hcColor"
      )[1].join(""),
    [
      appSettings.primaryColorLight,
      appSettings.secondaryColorLight,
      appSettings.primaryColorDark,
      appSettings.secondaryColorDark,
      linkColorLight,
      linkColorDark,
      highlightColorLight,
      highlightColorDark,
      primaryInvertedColorLight,
      secondaryInvertedColorLight,
      primaryInvertedColorDark,
      secondaryInvertedColorDark,
    ]
  );

  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);

  const pageTitleSuffix = `${
    appSettings.pageTitle || appSettings.title || appSettings.name
  }`;
  const normalizedPageTitle = isCustomPageTitle
    ? pageTitle
    : [pageTitle, pageTitleSuffix].filter(Boolean).join(" | ");

  const isArticle = appSettings.canonicalPageUrl.includes("/articles/");

  const ogImageContent = seoImageUrl || ssrLogoUrl;

  const customMainStyle = useMemo<React.CSSProperties>(
    () => ({
      ...(contentWidth && { maxWidth: contentWidth }),
      ...(noSpacing && { padding: 0 }),
    }),
    [contentWidth]
  );

  return (
    <div
      className={`${styles.layoutContainer} ${className || ""}`}
      itemScope
      itemType="http://schema.org/WebSite"
    >
      <meta
        itemProp="name"
        content={
          (pageTitle ?? appSettings.pageTitle) ||
          t("Help Center", appSettings.language)
        }
      />
      {ssrLogoUrl && (
        // While it is not displayed on the website it can be parsed by search engines to show site image
        <a href={baseUrl} itemProp="url" style={noDisplayStyle}>
          <Image
            src={ssrLogoUrl}
            data-original={ssrLogoUrl}
            itemProp="image"
            alt={
              (pageTitle ?? appSettings.pageTitle) ||
              t("Help Center", appSettings.language)
            }
            width={appSettings.logoWidth || 256}
            height={appSettings.logoHeight || 256}
          />
        </a>
      )}

      <Head>
        <title>{normalizedPageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="canonical"
          href={canonicalUrl || appSettings.canonicalPageUrl}
        />

        {appSettings.faviconUrl ? (
          <link rel="icon" href={appSettings.faviconUrl} />
        ) : (
          <>
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href="/apple-touch-icon.png"
            />
            <link rel="icon" href="/favicon.ico" />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href="/favicon-32x32.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="/favicon-16x16.png"
            />
          </>
        )}
        <meta name="theme-color" content="#ffffff" />

        {appSettings.hcGoogleSiteVerificationCode && (
          <meta
            name="google-site-verification"
            content={appSettings.hcGoogleSiteVerificationCode}
          />
        )}

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {ogImageContent && (
          <meta property="og:image" content={ogImageContent} />
        )}
        {appSettings.pageTitle ? (
          <meta property="og:site_name" content={appSettings.pageTitle} />
        ) : null}
        <meta property="og:url" content={appSettings.canonicalPageUrl} />
        {isArticle && <meta property="og:type" content="article" />}
        {articleLocale && <meta property="og:locale" content={articleLocale} />}

        {/* "html body" is needed to overwrite styles from StylesProvider */}
        <style>{`
          html body {
            background-color: var(--hcColor-secondary);
          }
        `}</style>
      </Head>

      <style suppressHydrationWarning>{colorsCss}</style>

      <AtlasSnippet
        appId={appSettings.appId}
        chatting={appSettings.chatting}
        recording={appSettings.sessionRecording}
        baseUrl={baseUrl}
      />

      <LayoutSidebarLight
        className={styles.menu}
        hasTitle={hasTitle}
        appSettings={appSettings}
        searchValue={searchValue}
        categoryName={articleCategoryName}
        {...layoutProps}
      />

      <main className={styles.main} style={customMainStyle}>
        <div className={styles.content}>
          {sidebar ? (
            <div className={styles.layoutColumns}>
              <div className={styles.layoutSidebarColumn}>{sidebar}</div>
              <div className={styles.layoutContentColumn}>{children}</div>
            </div>
          ) : (
            children
          )}
        </div>
        {!hideFooter && (
          <div className={cn(styles.content, styles.pushTop)}>
            <LayoutFooter
              className={cn(styles.layoutFooter, {
                [styles.layoutContentColumn]: sidebar,
              })}
              appSettings={appSettings}
            />
          </div>
        )}
      </main>
    </div>
  );
}

const noDisplayStyle: React.CSSProperties = { display: "none" };
export const minTwoColumnScreenWidth = 1100;
